/**
 * AdminMemberVisibilityPanel.tsx
 *
 * ADMIN-HIDE-001: Admin toggle to hide/show members on the lender dashboard
 * without revoking their consent.
 *
 * Access: Restricted to VISIBILITY_ADMIN_IDS in PrivacyKpiDashboard.tsx
 */

import { FC, useState, useEffect, useCallback } from 'react';
import { FiEye, FiEyeOff, FiRefreshCw, FiAlertTriangle, FiSearch } from 'react-icons/fi';

interface MemberOrgRow {
  member_org_id: string;
  member_id: string;
  org_id: string;
  status: string;
  admin_hidden: boolean;
  consent_granted_at: string;
  member_name?: string;
  email?: string;
}

const AdminMemberVisibilityPanel: FC = () => {
  const [members, setMembers] = useState<MemberOrgRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null); // member_id being toggled
  const [filter, setFilter] = useState<'all' | 'visible' | 'hidden'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Route through backend API (service role) to bypass RLS on members table
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/verascore/admin/member-visibility`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'VS-Client-Id': import.meta.env.VITE_VS_CLIENT_ID,
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `Failed: ${response.statusText}`);
      }

      const result = await response.json();
      const rows: MemberOrgRow[] = (result.data?.members || []).map((row: any) => ({
        member_org_id: row.member_org_id,
        member_id: row.member_id,
        org_id: row.org_id,
        status: row.status,
        admin_hidden: row.admin_hidden ?? false,
        consent_granted_at: row.consent_granted_at,
        member_name: row.member_name || row.member_id,
        email: row.email || null,
      }));

      setMembers(rows);
    } catch (err) {
      if (import.meta.env.DEV) console.error('[AdminMemberVisibilityPanel] Fetch error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load members');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleToggleVisibility = async (member: MemberOrgRow) => {
    const newHidden = !member.admin_hidden;
    setToggling(member.member_id);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/verascore/admin/member-visibility`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'VS-Client-Id': import.meta.env.VITE_VS_CLIENT_ID,
          },
          credentials: 'include',
          body: JSON.stringify({
            member_id: member.member_id,
            org_id: member.org_id,
            hidden: newHidden,
          }),
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `Failed: ${response.statusText}`);
      }

      // Update local state
      setMembers(prev =>
        prev.map(m =>
          m.member_id === member.member_id && m.org_id === member.org_id
            ? { ...m, admin_hidden: newHidden }
            : m
        )
      );
    } catch (err) {
      if (import.meta.env.DEV) console.error('[AdminMemberVisibilityPanel] Toggle error:', err);
      alert(`Failed to update visibility: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setToggling(null);
    }
  };

  // Filter + search
  const filtered = members.filter(m => {
    if (filter === 'visible' && m.admin_hidden) return false;
    if (filter === 'hidden' && !m.admin_hidden) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return (
        (m.member_name?.toLowerCase().includes(q)) ||
        (m.email?.toLowerCase().includes(q)) ||
        m.member_id.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const hiddenCount = members.filter(m => m.admin_hidden).length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FiAlertTriangle className="text-amber-600 mt-0.5 flex-shrink-0" size={18} />
          <div>
            <h3 className="font-semibold text-amber-800 text-sm">Admin-Only: Member Visibility Control</h3>
            <p className="text-amber-700 text-xs mt-1">
              Hiding a member removes them from the lender dashboard without revoking their consent.
              Consent status remains &quot;active&quot; for legal/audit purposes.
            </p>
          </div>
        </div>
      </div>

      {/* Controls row */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, or ID..."
              className="pl-8 pr-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
            />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="text-sm border border-slate-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All ({members.length})</option>
            <option value="visible">Visible ({members.length - hiddenCount})</option>
            <option value="hidden">Hidden ({hiddenCount})</option>
          </select>
        </div>
        <button
          onClick={fetchMembers}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-50"
        >
          <FiRefreshCw className={loading ? 'animate-spin' : ''} size={14} />
          Refresh
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <span className="ml-3 text-sm text-slate-500">Loading members...</span>
        </div>
      )}

      {/* Member table */}
      {!loading && filtered.length > 0 && (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-slate-600">Member</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">Email</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">Consent</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">Status</th>
                <th className="text-center px-4 py-2 font-medium text-slate-600">Visibility</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((member) => (
                <tr
                  key={`${member.member_id}-${member.org_id}`}
                  className={member.admin_hidden ? 'bg-red-50/50' : 'hover:bg-slate-50'}
                >
                  <td className="px-4 py-2.5">
                    <span className={member.admin_hidden ? 'text-slate-400 line-through' : 'text-slate-800'}>
                      {member.member_name || 'Unknown'}
                    </span>
                    <p className="text-xs text-slate-400 font-mono">{member.member_id.slice(0, 8)}...</p>
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">
                    {member.email || '—'}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">
                    {member.consent_granted_at
                      ? new Date(member.consent_granted_at).toLocaleDateString()
                      : '—'}
                  </td>
                  <td className="px-4 py-2.5">
                    {member.admin_hidden ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
                        <FiEyeOff size={10} /> Hidden
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                        <FiEye size={10} /> Visible
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <button
                      onClick={() => handleToggleVisibility(member)}
                      disabled={toggling === member.member_id}
                      className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                        member.admin_hidden
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-red-100 text-red-700 hover:bg-red-200'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                      title={member.admin_hidden ? 'Restore member to lender dashboard' : 'Hide member from lender dashboard'}
                    >
                      {toggling === member.member_id ? (
                        <span className="flex items-center gap-1">
                          <FiRefreshCw className="animate-spin" size={10} />
                          ...
                        </span>
                      ) : member.admin_hidden ? (
                        <span className="flex items-center gap-1"><FiEye size={10} /> Show</span>
                      ) : (
                        <span className="flex items-center gap-1"><FiEyeOff size={10} /> Hide</span>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && (
        <div className="text-center py-12 text-slate-500 text-sm">
          {members.length === 0
            ? 'No active member-org relationships found.'
            : 'No members match the current filter.'}
        </div>
      )}

      {/* Footer */}
      <div className="text-xs text-slate-400 text-center">
        {hiddenCount > 0 && (
          <p className="text-amber-600 font-medium">
            ⚠️ {hiddenCount} member{hiddenCount !== 1 ? 's' : ''} currently hidden from lender dashboard
          </p>
        )}
        <p className="mt-1">ADMIN-HIDE-001 • Consent preserved when hidden • Changes take effect immediately</p>
      </div>
    </div>
  );
};

export default AdminMemberVisibilityPanel;
